import { useCallback, useEffect, useRef, useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wrench, Loader2, Calculator, ShieldCheck, AlertTriangle, Truck,
  CheckCircle2, XCircle, StopCircle, RefreshCw, ChevronDown, ChevronRight,
  Play, Undo2, TrendingUp, TrendingDown, ArrowRight, Gauge,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { PeterbiltScanPanel } from "@/components/PeterbiltScanPanel";
import { DealerAskPanel } from "@/components/DealerAskPanel";
import { SheetRereadPanel } from "@/components/SheetRereadPanel";
import { BulkUploadCard } from "@/components/bulk/BulkUploadCard";
import { WorkedOutPanel } from "@/components/WorkedOutPanel";
import { WhatsMissingPanel } from "@/components/catchup/WhatsMissingPanel";
import { AiBackfillCard } from "@/components/catchup/AiBackfillCard";
import { LabelledValuesCard } from "@/components/catchup/LabelledValuesCard";
import { SearchNamesCard } from "@/components/catchup/SearchNamesCard";
import { TagNamingReport } from "@/components/catchup/TagNamingReport";

const n = (v) => (v === null || v === undefined ? 0 : v);

const Stat = ({ testid, big, label, tone = "slate", sub }) => (
  <div data-testid={testid} className={`rounded-xl border-2 p-3 ${
    tone === "green" ? "border-emerald-200 bg-emerald-50" :
    tone === "amber" ? "border-amber-200 bg-amber-50" :
    tone === "red" ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
    <div className={`font-display text-3xl font-black leading-none ${
      tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" :
      tone === "red" ? "text-[#B01E28]" : "text-[#1F1F1F]"}`}>{big}</div>
    <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-1.5">{label}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const SKIP_WORDS = {
  check_digit: "the check digit does not add up — never guessed",
  partial: "a partial VIN — not 17 characters",
  vin_unknown: "recorded as VIN-unknown — the plate could not be fully read",
  pre_1981: "built before VINs were standardised at 17 characters",
  forbidden_chars: "contains a character that never appears in a VIN",
  no_vin: "no VIN on the truck at all",
  length: "not a full 17-character VIN",
};

export default function CatchUpToolsPage() {
  const { has } = useAuth();
  const canManage = has("manage_donors");
  const [cohort, setCohort] = useState(null);
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [openTrucks, setOpenTrucks] = useBackState("openTrucks", false);
  const [openSkipped, setOpenSkipped] = useBackState("openSkipped", false);
  const [openLeft, setOpenLeft] = useBackState("openLeft", false);
  const [kpis, setKpis] = useState(null);
  const [run2, setRun2] = useState(null);        // the real run (writes)
  const [askRun, setAskRun] = useState(false);
  const [openDone, setOpenDone] = useBackState("openDone", true);
  const [undoing, setUndoing] = useState("");
  const poll = useRef(null);
  const poll2 = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, l, rr, k] = await Promise.all([
        api.get("/catch-up/vin-decode/cohort"),
        api.get("/catch-up/vin-decode/last"),
        api.get("/catch-up/vin-decode/run/last"),
        api.get("/catch-up/kpis"),
      ]);
      setCohort(c.data);
      setRun(l.data.run);
      setRun2(rr.data.run);
      setKpis(k.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the trucks"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // while a count is running, keep the line moving — it is a background job, so the page just watches
  useEffect(() => {
    if (run?.status !== "running") { clearInterval(poll.current); return; }
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/catch-up/vin-decode/count/${run.id}`);
        setRun(data);
      } catch { /* keep the last picture */ }
    }, 2500);
    return () => clearInterval(poll.current);
  }, [run?.status, run?.id]);

  useEffect(() => {
    if (run2?.status !== "running") { clearInterval(poll2.current); return; }
    poll2.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/catch-up/vin-decode/count/${run2.id}`);
        setRun2(data);
        if (data.status !== "running") { const { data: k } = await api.get("/catch-up/kpis"); setKpis(k); }
      } catch { /* keep the last picture */ }
    }, 2500);
    return () => clearInterval(poll2.current);
  }, [run2?.status, run2?.id]);

  const startRun = async () => {
    setAskRun(false);
    try {
      const { data } = await api.post("/catch-up/vin-decode/run", { confirm: true, from_count: run?.id || null });
      const { data: fresh } = await api.get(`/catch-up/vin-decode/count/${data.run_id}`);
      setRun2(fresh);
      toast.success(data.already_running ? "The run is already going" : "Running in the background — carry on working");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stopRun = async () => {
    try { await api.post(`/catch-up/vin-decode/run/${run2.id}/stop`); toast.success("Stopping — the trucks already done stay done"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const undoTruck = async (donorId, stock) => {
    setUndoing(donorId);
    try {
      await api.post(`/catch-up/vin-decode/run/${run2.id}/undo/${donorId}`);
      const { data } = await api.get(`/catch-up/vin-decode/count/${run2.id}`);
      setRun2(data);
      toast.success(`${stock} put back exactly as it was`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setUndoing("");
  };

  const startCount = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/catch-up/vin-decode/count");
      const { data: fresh } = await api.get(`/catch-up/vin-decode/count/${data.run_id}`);
      setRun(fresh);
      toast.success(data.already_running ? "A count is already running" : "Counting — nothing is being written");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setStarting(false);
  };

  const stopCount = async () => {
    try { await api.post(`/catch-up/vin-decode/count/${run.id}/stop`); toast.success("Stopping — what it has counted is kept"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const c = cohort?.counts || {};
  const rc = run?.counts || {};
  const running = run?.status === "running";

  return (
    <div data-testid="catch-up-page" className="max-w-5xl mx-auto px-4 py-6 space-y-6">

      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#1F1F1F] flex items-center gap-2.5">
          <Wrench className="w-9 h-9 text-[#B01E28]" />Catch-up tools
        </h1>
        <p className="text-base text-slate-600 mt-1.5 max-w-3xl">
          Loading years of paperwork in bulk — safely. Every tool here <b>shows you everything first</b>
          {" "}and changes nothing until you say go.
        </p>
      </div>

      {/* ============================ HOW MUCH IS DONE, HOW MUCH IS LEFT ============================
          His panel, 2026-10-18. Live counts off the real records — tap any line to open what's left. */}
      <section data-testid="kpi-panel" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
        <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Gauge className="w-5 h-5 shrink-0" />
            <h2 className="font-display text-base sm:text-lg font-bold leading-tight">How much is done, how much is left</h2>
          </div>
          <button data-testid="kpi-refresh" onClick={load} title="Count it again now"
            className="text-[10px] font-mono uppercase tracking-widest bg-white/15 hover:bg-white/25 px-2 py-1 rounded shrink-0">
            <RefreshCw className="w-3 h-3 inline mr-1" />Recount
          </button>
        </div>
        {!kpis ? (
          <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Counting…</div>
        ) : (
          <div className="p-3 sm:p-4 space-y-4">
            {["Trucks", "Parts", "Containers", "Lots"].map((grp) => {
              const rows = kpis.rows.filter((r) => r.group === grp);
              if (!rows.length) return null;
              return (
                <div key={grp} data-testid={`kpi-group-${grp.toLowerCase()}`}>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">{grp}</div>
                  <div className="space-y-1.5">
                    {rows.map((r) => (
                      <Link key={r.key} to={r.left_link} data-testid={`kpi-${r.key}`}
                        className="block rounded-lg border border-slate-200 hover:border-[#B01E28] hover:bg-red-50/40 transition-colors px-3 py-2.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-[#1F1F1F] truncate">{r.label}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`kpi-left-${r.key}`}>
                              {r.total === 0
                                ? <span className="text-slate-400">nothing to do here yet</span>
                                : r.left > 0
                                  ? <><b className="text-[#B01E28]">{r.left.toLocaleString()}</b> {r.left_words} — tap to open</>
                                  : <span className="text-emerald-700 font-semibold">all done</span>}
                            </div>
                            {r.note && <div data-testid={`kpi-note-${r.key}`} className="text-[11px] text-slate-400 mt-0.5">{r.note}</div>}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display text-xl font-black leading-none tabular-nums">
                              {r.pct === null ? "—" : `${r.pct}%`}
                            </div>
                            <div className="text-[11px] text-slate-500 tabular-nums">{r.done.toLocaleString()} / {r.total.toLocaleString()}</div>
                            {typeof r.week_move === "number" && r.week_move !== 0 && (
                              <div data-testid={`kpi-move-${r.key}`} className={`text-[11px] font-bold flex items-center gap-0.5 justify-end ${r.week_move > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                                {r.week_move > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {r.week_move > 0 ? "+" : "−"}{Math.abs(r.week_move).toLocaleString()} this week
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-[#B01E28]" style={{ width: `${r.pct || 0}%` }} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
            <p data-testid="kpi-week-note" className="text-[11px] text-slate-500">
              {kpis.days_of_history > 1 && kpis.week_from
                ? <>Movement is measured against {kpis.week_from}.</>
                : <>Week-on-week movement starts from today — nothing in the app was keeping a daily
                    record before now, so there is no earlier week to compare with. From tomorrow this
                    shows the real change.</>}
              {" "}Counted {kpis.read_at ? new Date(kpis.read_at).toLocaleString() : ""}.
            </p>
          </div>
        )}
      </section>

      {/* WHAT'S MISSING — truck by truck, part by part, next to the KPIs (his list, 22 Oct) */}
      <WhatsMissingPanel />

      {/* ---------------------------------------------------------------- HIS SMALL NAMING REPORT (23 Oct) */}
      <TagNamingReport />

      {/* THE THREE RULES — always visible, in his words */}
      <div data-testid="catch-up-rules" className="rounded-xl border-2 border-slate-200 bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">How these work</div>
        <ol className="mt-2 space-y-1.5 text-sm text-slate-700">
          <li><b>1.</b> Nothing is attached or written until you confirm a list you can read.</li>
          <li><b>2.</b> If anything is in doubt, it waits for you — it is never guessed.</li>
          <li><b>3.</b> Everything can be undone, one file or one truck at a time.</li>
        </ol>
      </div>

      {/* ---------------------------------------------------------------- BULK VIN DECODE */}
      <section data-testid="tool-vin-decode" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
        <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Truck className="w-5 h-5 shrink-0" />
            <h2 className="font-display text-lg font-bold truncate">Bulk VIN decode</h2>
          </div>
          <span data-testid="vin-decode-state" className="text-[10px] font-mono uppercase tracking-widest bg-blue-600 px-2 py-1 rounded shrink-0">Count only</span>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-slate-700">
            Fills <b>blank</b> year, make, model, engine and transmission details from the free government
            decoder. <b>Right now it only counts.</b> Nothing is written to a single truck until you have read
            the list below and told me to go ahead.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <Stat testid="cohort-trucks" big={n(c.trucks)} label="Trucks in the yard" />
            <Stat testid="cohort-askable" big={n(c.askable)} label="Can be asked" tone="green"
                  sub="full VIN, check digit adds up" />
            <Stat testid="cohort-skipped" big={n(c.trucks) - n(c.askable)} label="Skipped, never guessed" tone="amber"
                  sub="listed for a person" />
            <Stat testid="cohort-novin" big={n(c.no_vin)} label="No VIN at all" tone="red" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!running ? (
              <Button data-testid="start-count" onClick={startCount} disabled={starting || !c.askable}
                className="rounded-lg h-12 px-5 text-base font-bold bg-[#B01E28] hover:bg-[#8f1820]">
                {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Calculator className="w-5 h-5 mr-2" />}
                Count first — change nothing
              </Button>
            ) : (
              <Button data-testid="stop-count" onClick={stopCount} variant="outline"
                className="rounded-lg h-12 px-5 text-base font-bold border-[#B01E28] text-[#B01E28]">
                <StopCircle className="w-5 h-5 mr-2" />Stop the count
              </Button>
            )}
            <Button data-testid="refresh-catch-up" variant="outline" onClick={load} className="rounded-lg h-12 px-4 border-slate-300">
              <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
            </Button>
            {!c.askable && cohort && (
              <span data-testid="nothing-to-ask" className="text-sm text-slate-500">
                No truck here has a full VIN the decoder can be asked about.
              </span>
            )}
          </div>

          {running && (
            <div data-testid="count-progress" className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Counting {run.scanned} of {run.total} trucks — in the background, nothing is being written
              </div>
              <div className="mt-2 h-2 rounded-full bg-blue-200 overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round(100 * n(run.scanned) / Math.max(1, n(run.total)))}%` }} />
              </div>
              <p className="text-[11px] text-blue-900/70 mt-1.5">Carry on working — this does not slow the app down. Come back any time.</p>
            </div>
          )}

          {run && !running && (
            <div data-testid="count-report" className="space-y-4">
              <div data-testid="wrote-nothing-banner" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <b>Nothing has been written.</b> This count read {n(run.scanned)} truck{n(run.scanned) === 1 ? "" : "s"},
                  asked the decoder, and worked out what it <i>would</i> fill. Not one truck was changed — the only thing
                  saved is this report. {run.status === "stopped" && <b>You stopped it part-way; what it counted is below.</b>}
                  <div className="text-[11px] text-emerald-900/70 mt-0.5">
                    Counted {run.finished_at ? new Date(run.finished_at).toLocaleString() : ""} by {run.started_by}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <Stat testid="would-fill" big={n(rc.values_that_would_fill)} label="Would be FILLED" tone="green"
                      sub="the box is empty today" />
                <Stat testid="would-refresh" big={n(rc.values_that_would_refresh)} label="Would be REFRESHED" tone="green"
                      sub="still the old decoder's own value" />
                <Stat testid="would-sit-beside" big={n(rc.values_put_beside)} label="Would be PUT BESIDE" tone="amber"
                      sub="no record of who typed it — yours stays" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <Stat testid="would-gain" big={n(rc.trucks_that_would_gain)} label="Trucks that would change" />
                <Stat testid="trucks-beside" big={n(rc.trucks_with_something_beside)} label="Trucks with a note beside" />
                <Stat testid="left-alone" big={n(rc.left_alone)} label="Never touched" tone="amber"
                      sub="a person entered, corrected or checked it" />
                <Stat testid="nothing-came-back" big={n(rc.nothing_came_back)} label="Decoder had nothing" />
              </div>

              {rc.own_tick_rows > 0 && (
                <div data-testid="own-tick-note" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <b>{rc.own_tick_rows} engine / transmission value{rc.own_tick_rows === 1 ? "" : "s"}</b> would be filled —
                    and each one still needs <b>your ✓ on that truck</b>, one at a time. They land in
                    {" "}<Link to="/donors?decode_flag=engine_unchecked" className="underline font-bold">Engine not checked</Link>,
                    never ticked by a machine.
                  </div>
                </div>
              )}

              {Object.keys(rc.by_field || {}).length > 0 && (
                <div data-testid="by-field" className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Which boxes would fill</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(rc.by_field).sort((a, b) => b[1] - a[1]).map(([label, count]) => (
                      <span key={label} data-testid={`field-count-${label.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-xs font-semibold rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                        {label} <b className="text-[#B01E28]">{count}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* the exact trucks and the exact fields */}
              <div className="rounded-lg border border-slate-200 bg-white">
                <button data-testid="toggle-trucks" onClick={() => setOpenTrucks((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left">
                  <span className="font-bold text-sm flex items-center gap-1.5">
                    {openTrucks ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Exactly which trucks and which boxes ({n(run.trucks_total)})
                  </span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                </button>
                {openTrucks && (
                  <div data-testid="truck-list" className="divide-y divide-slate-100 border-t border-slate-100">
                    {(run.trucks || []).map((t) => (
                      <div key={t.donor_id} data-testid={`count-truck-${t.stock_number}`} className="px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/donors/${t.donor_id}`} className="font-mono font-bold text-sm text-[#B01E28] hover:underline">{t.stock_number}</Link>
                          <span className="text-sm text-slate-700">{t.title}</span>
                          <span className="font-mono text-[11px] text-slate-400">{t.vin}</span>
                        </div>
                        <div className="mt-1.5 space-y-1">
                          {t.would_fill.map((w) => (
                            <div key={w.field} data-testid={`fill-${t.stock_number}-${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                              <span className="text-emerald-700 font-bold">would fill</span>
                              <span className="font-semibold">{w.label}</span>
                              <span className="text-slate-400">blank →</span>
                              <span className="font-mono bg-emerald-50 border border-emerald-200 rounded px-1.5">{w.decode_said}</span>
                              {w.own_tick && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">needs your ✓</span>}
                            </div>
                          ))}
                          {(t.would_refresh || []).map((w) => (
                            <div key={w.field} data-testid={`refresh-${t.stock_number}-${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                              <span className="text-sky-700 font-bold">would refresh</span>
                              <span className="font-semibold">{w.label}</span>
                              <span className="font-mono text-slate-500 line-through">{String(w.now)}</span>
                              <ArrowRight className="w-3 h-3 text-slate-400" />
                              <span className="font-mono bg-sky-50 border border-sky-200 rounded px-1.5">{w.decode_said}</span>
                              {w.own_tick && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">needs your ✓</span>}
                            </div>
                          ))}
                          {(t.would_sit_beside || []).map((w) => (
                            <div key={w.field} data-testid={`beside-${t.stock_number}-${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                              <span className="text-amber-700 font-bold">would sit beside</span>
                              <span className="font-semibold">{w.label}</span>
                              <span className="font-mono text-slate-700">{String(w.now)}</span>
                              <span className="text-slate-400">stays · the VIN decode says</span>
                              <span className="font-mono bg-amber-50 border border-amber-200 rounded px-1.5">{w.decode_said}</span>
                            </div>
                          ))}
                          {t.left_alone.map((w) => (
                            <div key={w.field} data-testid={`left-${t.stock_number}-${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                              <span className="text-slate-500 font-bold">never touched</span>
                              <span className="font-semibold">{w.label}</span>
                              <span className="font-mono text-slate-600">{String(w.now)}</span>
                              <span className="text-slate-400">— {w.why}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(run.trucks || []).length === 0 && (
                      <div className="px-3 py-3 text-sm text-slate-500">Nothing would fill on any truck.</div>
                    )}
                  </div>
                )}
              </div>

              {/* why values were left alone */}
              {Object.keys(rc.left_alone_why || {}).length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white">
                  <button data-testid="toggle-left-alone" onClick={() => setOpenLeft((v) => !v)}
                    className="w-full flex items-center gap-1.5 px-3 py-2.5 text-left font-bold text-sm">
                    {openLeft ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Why values were left alone
                  </button>
                  {openLeft && (
                    <div data-testid="left-alone-why" className="px-3 pb-3 space-y-1 text-sm">
                      {Object.entries(rc.left_alone_why).map(([why, count]) => (
                        <div key={why} className="flex items-start gap-2">
                          <span className="font-black text-amber-700">{count}</span>
                          <span className="text-slate-700">{why}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* the skipped VINs */}
              <div className="rounded-lg border border-slate-200 bg-white">
                <button data-testid="toggle-skipped" onClick={() => setOpenSkipped((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left">
                  <span className="font-bold text-sm flex items-center gap-1.5">
                    {openSkipped ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Skipped — never guessed ({n(run.skipped_total)})
                  </span>
                  <XCircle className="w-4 h-4 text-amber-600 shrink-0" />
                </button>
                {openSkipped && (
                  <div data-testid="skipped-list" className="divide-y divide-slate-100 border-t border-slate-100">
                    {(run.skipped || []).map((sk) => (
                      <div key={sk.donor_id} data-testid={`skipped-${sk.stock_number}`} className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
                        <Link to={`/donors/${sk.donor_id}`} className="font-mono font-bold text-[#B01E28] hover:underline">{sk.stock_number}</Link>
                        <span className="text-slate-700">{sk.title}</span>
                        <span className="font-mono text-[11px] text-slate-400">{sk.vin || "no VIN"}</span>
                        <span className="ml-auto text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                          {SKIP_WORDS[sk.kind] || sk.kind}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* HIS BUTTON. Nothing automatic — he reads the count above, then presses this. */}
              <div data-testid="ready-to-run" className="rounded-lg border-2 border-[#B01E28] bg-red-50 p-3">
                <div className="font-bold text-sm text-[#B01E28]">Happy with that? Then run it</div>
                <p className="text-sm text-slate-700 mt-1">
                  It writes exactly what is listed above and nothing else: <b>fills</b> the empty boxes,
                  <b> refreshes</b> the old decoder's own values (keeping the old answer in the truck's
                  history), and <b>puts the new answer beside</b> anything with no record of who typed it.
                  Engine and transmission still wait for your ✓ on each truck. <b>Undo sits beside every
                  truck</b> afterwards.
                </p>
                {run2?.status === "running" ? (
                  <Button data-testid="stop-run" onClick={stopRun} variant="outline"
                    className="mt-2 rounded-lg h-12 px-5 font-bold border-[#B01E28] text-[#B01E28]">
                    <StopCircle className="w-5 h-5 mr-2" />Stop the run
                  </Button>
                ) : (
                  <Button data-testid="fill-the-blanks" onClick={() => setAskRun(true)}
                    className="mt-2 rounded-lg h-12 px-5 text-base font-bold bg-[#B01E28] hover:bg-[#8f1820]">
                    <Play className="w-5 h-5 mr-2" />Re-decode every truck
                  </Button>
                )}
              </div>
            </div>
          )}

          {!run && !running && (
            <p data-testid="no-count-yet" className="text-sm text-slate-500">
              No count has been run yet. The button above reads the trucks, asks the free decoder and shows you
              the whole list — it writes nothing.
            </p>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------------- WHAT THE RUN DID */}
      {run2 && (
        <section data-testid="run-report" className="rounded-xl border-2 border-[#B01E28] bg-white overflow-hidden">
          <div className="bg-[#B01E28] text-white px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Play className="w-5 h-5 shrink-0" />
              <h2 className="font-display text-lg font-bold truncate">What the re-decode did</h2>
            </div>
            <span data-testid="run-state" className="text-[10px] font-mono uppercase tracking-widest bg-white/20 px-2 py-1 rounded shrink-0">
              {run2.status === "running" ? "Running" : run2.status === "stopped" ? "Stopped" : "Finished"}
            </span>
          </div>
          <div className="p-4 space-y-4">
            {run2.status === "running" && (
              <div data-testid="run-progress" className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                <div className="flex items-center gap-2 text-[#B01E28] font-bold text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {run2.scanned} of {run2.total} trucks — in the background. Carry on working.
                </div>
                <div className="mt-2 h-2 rounded-full bg-red-200 overflow-hidden">
                  <div className="h-full bg-[#B01E28] transition-all" style={{ width: `${Math.round(100 * n(run2.scanned) / Math.max(1, n(run2.total)))}%` }} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <Stat testid="run-filled" big={n(run2.counts?.values_filled)} label="Boxes filled" tone="green" />
              <Stat testid="run-refreshed" big={n(run2.counts?.values_refreshed)} label="Boxes refreshed" tone="green" />
              <Stat testid="run-beside" big={n(run2.counts?.values_put_beside)} label="Notes put beside" tone="amber" />
              <Stat testid="run-untouched" big={n(run2.counts?.left_alone)} label="Never touched" />
            </div>
            <p className="text-[11px] text-slate-500" data-testid="run-stamp">
              Run by {run2.started_by} · started {run2.started_at ? new Date(run2.started_at).toLocaleString() : ""}
              {run2.finished_at ? ` · finished ${new Date(run2.finished_at).toLocaleString()}` : ""}
              {n(run2.counts?.own_tick_rows) > 0 && <> · <b>{run2.counts.own_tick_rows} engine / transmission value(s) still need your ✓</b> in <Link className="underline" to="/donors?decode_flag=engine_unchecked">Engine not checked</Link></>}
            </p>

            <div className="rounded-lg border border-slate-200 bg-white">
              <button data-testid="toggle-run-trucks" onClick={() => setOpenDone((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left">
                <span className="font-bold text-sm flex items-center gap-1.5">
                  {openDone ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Every truck it changed ({n(run2.trucks_total)}) — with Undo
                </span>
                <Undo2 className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
              {openDone && (
                <div data-testid="run-truck-list" className="divide-y divide-slate-100 border-t border-slate-100">
                  {(run2.trucks || []).map((t) => (
                    <div key={t.donor_id} data-testid={`run-truck-${t.stock_number}`} className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/donors/${t.donor_id}`} className="font-mono font-bold text-sm text-[#B01E28] hover:underline">{t.stock_number}</Link>
                        <span className="text-sm text-slate-700">{t.title}</span>
                        {t.undone ? (
                          <span data-testid={`undone-${t.stock_number}`} className="ml-auto text-[11px] font-bold uppercase text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">put back</span>
                        ) : (
                          <button data-testid={`undo-${t.stock_number}`} disabled={undoing === t.donor_id}
                            onClick={() => undoTruck(t.donor_id, t.stock_number)}
                            className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-300 hover:border-[#B01E28] hover:text-[#B01E28] text-xs font-bold px-2.5 h-8">
                            {undoing === t.donor_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}Undo this truck
                          </button>
                        )}
                      </div>
                      <div className="mt-1.5 space-y-1">
                        {(t.filled || []).map((w) => (
                          <div key={`f${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                            <span className="text-emerald-700 font-bold">filled</span>
                            <span className="font-semibold">{w.label}</span>
                            <span className="font-mono bg-emerald-50 border border-emerald-200 rounded px-1.5">{w.now}</span>
                          </div>
                        ))}
                        {(t.refreshed || []).map((w) => (
                          <div key={`r${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                            <span className="text-sky-700 font-bold">refreshed</span>
                            <span className="font-semibold">{w.label}</span>
                            <span className="font-mono text-slate-500 line-through">{String(w.was ?? w.kept ?? "")}</span>
                            <ArrowRight className="w-3 h-3 text-slate-400" />
                            <span className="font-mono bg-sky-50 border border-sky-200 rounded px-1.5">{w.now}</span>
                          </div>
                        ))}
                        {(t.beside || []).map((w) => (
                          <div key={`b${w.field}`} className="text-[13px] flex flex-wrap items-center gap-1.5">
                            <span className="text-amber-700 font-bold">put beside</span>
                            <span className="font-semibold">{w.label}</span>
                            <span className="font-mono text-slate-700">{String(w.kept ?? "")}</span>
                            <span className="text-slate-400">kept · the VIN decode says</span>
                            <span className="font-mono bg-amber-50 border border-amber-200 rounded px-1.5">{w.decode_said}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(run2.trucks || []).length === 0 && (
                    <div className="px-3 py-3 text-sm text-slate-500">Nothing has changed on any truck.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* THE CONFIRM — the only way the run ever starts */}
      <Dialog open={askRun} onOpenChange={setAskRun}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="run-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#B01E28]">
              <Play className="w-5 h-5" />Re-decode every truck?
            </DialogTitle>
            <DialogDescription>
              This one writes. It will fill <b>{n(rc.values_that_would_fill)}</b> empty box(es),
              refresh <b>{n(rc.values_that_would_refresh)}</b> old decoder value(s) and put
              <b> {n(rc.values_put_beside)}</b> note(s) beside values with no record of who typed them.
              Anything a person entered, corrected or checked is not touched. Undo sits beside every truck.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="run-cancel" onClick={() => setAskRun(false)}>Not yet</Button>
            <Button data-testid="run-confirm" onClick={startRun} className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
              <Play className="w-4 h-4 mr-1.5" />Yes — run it now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------- RE-READ EVERY SHEET */}
      <SheetRereadPanel canManage={canManage} testid="sheet-reread" />

      {/* ---------------------------------------------------------------- PETERBILT SCAN */}
      <PeterbiltScanPanel canManage={canManage} testid="peterbilt-scan" />

      {/* ---------------------------------------------------------------- THE DEALER ASK SHEET */}
      <DealerAskPanel canManage={canManage} testid="dealer-ask" />

      {/* ---------------------------------------------------------------- BULK UPLOAD */}
      <BulkUploadCard canManage={canManage} />

      {/* ---------------------------------------------------------------- WORKED-OUT VALUES */}
      <WorkedOutPanel canManage={canManage} />

      {/* ---------------------------------------------------------------- THE AI BACKFILL, BEHIND HIS COUNT */}
      <AiBackfillCard />

      {/* ---------------------------------------------------------------- DATES AND RATINGS, COUNT FIRST, HE PRESSES GO */}
      <LabelledValuesCard />

      {/* ---------------------------------------------------------------- NAMES WITH NO SEARCH NAME, COUNT FIRST, HE PRESSES GO */}
      <SearchNamesCard />

    </div>
  );
}
